import { Amplify } from '@aws-amplify/core';
import { Auth } from '@aws-amplify/auth';
import { awsConfig } from '../config/aws';
import { User, AuthToken, UserRole } from '../types/auth';
import { apiService } from './apiService';

// Configure Amplify with real Cognito
Amplify.configure({
  Auth: awsConfig.Auth
});

export class AuthService {
  private cognitoUser: any = null; // Store Cognito user for NEW_PASSWORD_REQUIRED challenge

  async requestPasswordReset(email: string): Promise<void> {
    try {
      await Auth.forgotPassword(email);
    } catch (error: any) {
      // Avoid leaking whether the user exists
      const code = error?.code || error?.name;
      if (code === 'UserNotFoundException') {
        return;
      }

      console.error('Request password reset error:', error);
      throw new Error(error.message || 'Could not request password reset');
    }
  }

  async confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void> {
    try {
      await Auth.forgotPasswordSubmit(email, code, newPassword);
    } catch (error: any) {
      const errCode = error?.code || error?.name;

      let message = error.message || 'Password reset failed';
      if (errCode === 'CodeMismatchException') {
        message = 'Invalid verification code';
      } else if (errCode === 'ExpiredCodeException') {
        message = 'Verification code has expired. Please request a new one.';
      } else if (errCode === 'InvalidPasswordException') {
        message = 'Password does not meet requirements';
      } else if (errCode === 'LimitExceededException') {
        message = 'Too many attempts. Please try again later.';
      }

      console.error('Confirm password reset error:', error);
      throw new Error(message);
    }
  }

  async login(email: string, password: string): Promise<{ user: User; token: AuthToken; requiresPasswordChange?: boolean }> {
    try {
      const signInResult = await Auth.signIn(email, password);
      
      // Check if password change is required
      if (signInResult.challengeName === 'NEW_PASSWORD_REQUIRED') {
        // Store the Cognito user object for completing the challenge
        this.cognitoUser = signInResult;
        return {
          user: {} as User,
          token: {} as AuthToken,
          requiresPasswordChange: true
        };
      }

      const session = await Auth.currentSession();
      const currentUser = await Auth.currentAuthenticatedUser();
      
      const idToken = session.getIdToken().getJwtToken();
      const accessToken = session.getAccessToken().getJwtToken();
      const refreshToken = session.getRefreshToken().getToken();
      
      // Extract user attributes from token
      const payload = session.getIdToken().payload;
      
      // Check for role in custom attribute or Cognito groups
      let role = payload['custom:role'] as string;
      if (!role) {
        // Fallback to checking Cognito groups
        const groups = payload['cognito:groups'] as string[] || [];
        role = groups.includes('administrators') ? UserRole.ADMINISTRATOR : UserRole.PRACTITIONER;
      }
      
      const user: User = {
        id: currentUser.username,
        email: payload.email as string || email,
        name: payload.name as string || '',
        phoneNumber: payload.phone_number as string || '',
        profilePhotoKey: payload['custom:profile_photo_key'] as string || undefined,
        role: role as UserRole,
        status: 'active' as any,
        createdAt: new Date().toISOString()
      };

      const token: AuthToken = {
        accessToken,
        idToken,
        refreshToken,
        expiresIn: 3600
      };

      return { user, token };
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.message || 'Login failed');
    }
  }

  async logout(): Promise<void> {
    try {
      await Auth.signOut();
      this.cognitoUser = null;
    } catch (error: any) {
      console.error('Logout error:', error);
      throw new Error(error.message || 'Logout failed');
    }
  }

  async completeNewPassword(newPassword: string): Promise<{ user: User; token: AuthToken }> {
    try {
      if (!this.cognitoUser) {
        throw new Error('No pending password change. Please log in again.');
      }

      // Complete the NEW_PASSWORD_REQUIRED challenge
      await Auth.completeNewPassword(this.cognitoUser, newPassword);
      this.cognitoUser = null; // Clear the stored user

      const session = await Auth.currentSession();
      const currentUser = await Auth.currentAuthenticatedUser();
      
      const idToken = session.getIdToken().getJwtToken();
      const accessToken = session.getAccessToken().getJwtToken();
      const refreshToken = session.getRefreshToken().getToken();
      
      const payload = session.getIdToken().payload;
      
      let role = payload['custom:role'] as string;
      if (!role) {
        const groups = payload['cognito:groups'] as string[] || [];
        role = groups.includes('administrators') ? UserRole.ADMINISTRATOR : UserRole.PRACTITIONER;
      }
      
      const user: User = {
        id: currentUser.username,
        email: payload.email as string || '',
        name: payload.name as string || '',
        phoneNumber: payload.phone_number as string || '',
        profilePhotoKey: payload['custom:profile_photo_key'] as string || undefined,
        role: role as UserRole,
        status: 'active' as any,
        createdAt: new Date().toISOString()
      };

      const token: AuthToken = {
        accessToken,
        idToken,
        refreshToken,
        expiresIn: 3600
      };

      return { user, token };
    } catch (error: any) {
      console.error('Complete new password error:', error);
      throw new Error(error.message || 'Password change failed');
    }
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(user, oldPassword, newPassword);
    } catch (error: any) {
      console.error('Change password error:', error);
      throw new Error(error.message || 'Password change failed');
    }
  }

  async getCurrentSession(): Promise<{ user: User; token: AuthToken } | null> {
    try {
      const session = await Auth.currentSession();
      const currentUser = await Auth.currentAuthenticatedUser();
      
      const payload = session.getIdToken().payload;
      
      // Check for role in custom attribute or Cognito groups
      let role = payload['custom:role'] as string;
      if (!role) {
        // Fallback to checking Cognito groups
        const groups = payload['cognito:groups'] as string[] || [];
        role = groups.includes('administrators') ? UserRole.ADMINISTRATOR : UserRole.PRACTITIONER;
      }

      const token: AuthToken = {
        accessToken: session.getAccessToken().getJwtToken(),
        idToken: session.getIdToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken(),
        expiresIn: 3600
      };

      // Build user from JWT token claims (fallback)
      let user: User = {
        id: currentUser.username,
        email: payload.email as string || '',
        name: payload.name as string || '',
        phoneNumber: payload.phone_number as string || '',
        profilePhotoKey: payload['custom:profile_photo_key'] as string || undefined,
        role: role as UserRole,
        status: 'active' as any,
        createdAt: new Date().toISOString()
      };

      // Try to fetch fresh user data from the backend API
      // This ensures we get the latest name/photo after profile updates
      try {
        const freshUser = await apiService.get<User>('/auth/profile');
        if (freshUser) {
          // Merge fresh data but preserve role from JWT (more reliable)
          user = {
            ...user,
            ...freshUser,
            role: role as UserRole, // Keep role from JWT token
          };
        }
      } catch (profileError) {
        // If profile fetch fails, continue with JWT token data
        console.warn('Could not fetch fresh profile data, using JWT claims:', profileError);
      }

      return { user, token };
    } catch (error) {
      return null;
    }
  }

  async updateProfile(name: string, photoFile?: File): Promise<User> {
    try {
      let photoContent: string | undefined;
      let photoFileName: string | undefined;

      if (photoFile) {
        // Convert file to base64
        photoContent = await this.fileToBase64(photoFile);
        photoFileName = photoFile.name;
      }

      const response = await apiService.put<User>('/auth/profile', {
        name,
        photoContent,
        photoFileName,
      });

      return response;
    } catch (error: any) {
      console.error('Update profile error:', error);
      throw new Error(error.message || 'Failed to update profile');
    }
  }

  async getProfilePhotoUrl(): Promise<string | null> {
    try {
      const response = await apiService.get<{ url: string }>('/auth/profile/photo-url');
      return response.url;
    } catch (error) {
      return null;
    }
  }

  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix (e.g., "data:image/png;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
}

export const authService = new AuthService();
